import React from "react";
import * as motion from "motion/react-client";
import './App.css'; // Import the CSS file

const App = () => {
  return (
    <div className="font-poppins bg-off-white text-navy-blue">
      {/* Header */}
      <header className="bg-primary-green fixed top-0 w-full z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center py-4">
          <div className="text-xl font-bold text-off-white">GSVcode</div>
          <nav>
            <ul className="flex space-x-6">
              <li>
                <a href="#about" className="nav-item">
                  About Us
                </a>
              </li>
              <li>
                <a href="#services" className="nav-item">
                  Services
                </a>
              </li>
              <li>
                <a href="#vision" className="nav-item">
                  Vision
                </a>
              </li>
              <li>
                <a href="#cta" className="nav-item">
                  Get Involved
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section
        id="hero"
        className="bg-gradient-to-r from-bright-yellow to-primary-green text-center py-20 mt-16"
      >
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          {/* Text Section */}
          <motion.div
            className="text-center md:text-left"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-bold mb-4 text-navy-blue">
              Building Solutions That Truly Matter
            </h1>
            <p className="text-lg mb-6 text-navy-blue">
              At <strong>GSVcode</strong>, we make life, travel, and business
              easier with innovative software solutions. Let us help you focus
              on what truly matters.
            </p>
            <a
              href="#cta"
              className="bg-primary-green text-off-white font-semibold py-3 px-8 rounded-md shadow-lg hover:bg-light-green hover:shadow-xl transition-all duration-300"
            >
              Join Us Today
            </a>
          </motion.div>
        

          {/* Image Section */}
          <motion.div
            className="flex justify-center md:justify-end"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="/images/cartoon-home-laptop.png"
              alt="Illustration of software solutions"
              className="rounded-lg shadow-lg max-w-full h-auto"
            />
          </motion.div>
        </div>
      </section>
      
      {/* About Us Section */}
      <section id="about" className="py-16 bg-off-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-navy-blue mb-8">
            About Us
          </h2>
          <p className="text-lg text-navy-blue leading-relaxed">
            At <strong>GSVcode</strong>, we are passionate about building
            software solutions that solve real problems for people and
            businesses. Our mission is to make life, travel, and business easier
            by providing tools that let you focus on what truly matters.
          </p>
          <p className="text-lg text-navy-blue leading-relaxed mt-4">
            We pride ourselves on creating applications that are scalable from
            the ground up, allowing for hyperscale growth when success strikes.
            Using our consultancy expertise, we ensure we address challenges at
            their core—not just fight the symptoms.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-primary-green text-off-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            className="text-4xl font-bold text-center mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            Our Services
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="p-6 bg-card-color rounded-lg shadow-md border border-card-border"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-semibold text-navy-blue mb-4">
                Problem-Solving
              </h3>
              <p className="text-navy-blue">
                We dive deep into your challenges to create solutions that
                address the root cause—not just the symptoms.
              </p>
            </motion.div>

            <motion.div
              className="p-6 bg-card-color rounded-lg shadow-md border border-card-border"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-semibold text-navy-blue mb-4">
                MVP Development
              </h3>
              <p className="text-navy-blue">
                Validate your ideas quickly with a functional minimum viable
                product to gather feedback and iterate.
              </p>
            </motion.div>

            <motion.div
              className="p-6 bg-card-color rounded-lg shadow-md border border-card-border"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-semibold text-navy-blue mb-4">
                Cloud & AI Integration
              </h3>
              <p className="text-navy-blue">
                Build scalable apps and enhance functionality with cutting-edge
                AI solutions and cloud services.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="py-16 bg-off-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-navy-blue mb-8">
            Our Vision
          </h2>
          <p className="text-lg text-navy-blue leading-relaxed">
            We envision a future where GSVcode operates a diverse portfolio of
            apps, tackling real-world problems across various domains. From
            simplifying daily routines to revolutionizing business workflows, we
            aim to empower individuals and businesses with scalable, efficient
            solutions.
          </p>
          <p className="text-lg text-navy-blue leading-relaxed mt-4">
            In addition to building and deploying apps, we also provide
            consultancy services to help businesses scale their applications.
            With expertise in cloud services, AI integration, and search engine
            development, we ensure our clients achieve success.
          </p>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section
        id="cta"
        className="py-16 bg-primary-green text-off-white text-center"
      >
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-4">Get Involved</h2>
          <p className="text-lg mb-6">
            Join our journey to create impactful software solutions. Sign up for
            updates, try our apps, and share your feedback to help us improve.
          </p>
          <a
            href="#"
            className="bg-primary-green text-off-white font-semibold py-3 px-8 rounded-md shadow-lg hover:bg-bright-green hover:shadow-xl transition-all duration-300"
          >
            Sign Up for Updates
          </a>
        </div>
      </section>
    </div>
  );
};

export default App;
